'use client';

import { useEffect, useState } from 'react';

import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import PriceTypeMessage from '@/components/PriceTypeMessage';
import { Skeleton } from '@/components/Skeleton';
import { useUIResourcePool } from '@/ducks/common/resources';
import { selectSecondarySailors, type TSailorInfoWithInsurance } from '@/ducks/travelParty/selectors';
import { buildTripInfo } from '@/features/uplift/adapter';
import { useIsUpliftApplicable } from '@/features/uplift/hooks';
import { selectUpliftIsReady } from '@/features/uplift/selectors';
import getSymbolFromCurrenciesData from '@/helpers/util/currency/currencySymbols';
import { imitateClickOnKeyEvent } from '@/hooks/useOnKeyDown';
import { type TCurrencyCode } from '@/infra/types/common';
import { FiltersPriceType } from '@/infra/types/common/filters';
import { type TSailingData } from '@/infra/types/voyageInfo/sailing';
import { useAppSelector } from '@/store';
import tagmanager from '@/tagmanager';

import UpliftReceiver from './Receiver';
import UpliftRoot from './Root';
import UpliftTooltip from './Tooltip';

import './Uplift.scss';

const defaultSkeletonProps = {
  height: '21px',
  width: '150px',
};

const {
  tracker: { createTracker },
  trackerConstants,
} = tagmanager;

const trackEvent = createTracker({});

const UI_RESOURCE_POOL = {
  fromLabel: 'ItineraryRefinementCard.from',
  monthLabel: 'uplift.month',
  orFromLabel: 'uplift.startPrice',
} as const;

type Props = {
  ariaHidden?: boolean;
  ariaLabel?: string;
  cabinName?: string;
  className?: string;
  currencyCode?: TCurrencyCode;
  customIcon?: React.ReactNode;
  flyout?: string;
  hasPriceTypeLabel?: boolean;
  insuranceAmount?: number;
  isCruiseOptionPrice?: boolean;
  isHorizontal?: boolean;
  isLoadUpliftDisabled?: boolean;
  isModalDisabled?: boolean;
  isTaxesIncluded?: boolean;
  isTooltipDisabled?: boolean;
  onTrack?: () => void;
  price?: number;
  priceType?: FiltersPriceType;
  primarySailor?: TSailorInfoWithInsurance;
  renderWithWrapper?: (element: React.ReactNode) => React.ReactNode;
  sailing?: TSailingData;
  showOrFromLabel?: boolean;
  skeletonProps?: {
    baseColor?: string;
    height?: string;
    highlightColor?: string;
    width?: string;
  };
  trackerEventCategory?: string;
};

const Uplift = ({
  ariaHidden = false,
  ariaLabel,
  cabinName = '',
  className,
  currencyCode = 'USD',
  customIcon = '',
  flyout,
  hasPriceTypeLabel,
  insuranceAmount = 0,
  isCruiseOptionPrice,
  isHorizontal,
  isLoadUpliftDisabled,
  isModalDisabled,
  isTaxesIncluded,
  isTooltipDisabled = true,
  onTrack,
  price,
  priceType = FiltersPriceType.perCabin,
  primarySailor,
  renderWithWrapper = (e) => e,
  sailing,
  showOrFromLabel = true,
  skeletonProps = defaultSkeletonProps,
  trackerEventCategory,
}: Props) => {
  const isUpliftReady = useAppSelector(selectUpliftIsReady);
  const additionalSailors = useAppSelector(selectSecondarySailors);
  const priceWithCents = Math.ceil((price || 0) * 100);
  const insuranceWithCents = insuranceAmount * 100;
  const [tripInfo, setTripInfo] = useState<Record<string, unknown>>();
  const [upliftPrice, setUpliftPrice] = useState('');

  const canStartLoad =
    !isLoadUpliftDisabled && isUpliftReady && !isEmpty(sailing?.ports) && !isEmpty(sailing?.startDate);

  useEffect(() => {
    if (canStartLoad) {
      const next = buildTripInfo(
        priceWithCents,
        insuranceWithCents,
        sailing!,
        primarySailor!,
        cabinName,
        additionalSailors,
      );
      setTripInfo((prev) => (!isEqual(next, prev) ? next : prev));
    }
  }, [additionalSailors, cabinName, canStartLoad, insuranceWithCents, priceWithCents, primarySailor, sailing]);

  const { fromLabel, monthLabel, orFromLabel } = useUIResourcePool(UI_RESOURCE_POOL);
  const startLabel = showOrFromLabel ? orFromLabel : fromLabel;

  const isUpliftApplicable = useIsUpliftApplicable({
    amount: price!,
    currencyCode,
    startDate: (sailing?.startDate || sailing?.embarkDate)!,
  });
  if (!isUpliftApplicable) return null;

  const trackUplift = () => {
    const label = `${trackerConstants.EVENT_LABELS.UPLIFT_INFO}${flyout ? `|${flyout}` : ''}`;
    tagmanager.tracker.common.trackIconClick({
      category: trackerEventCategory,
      label,
    });
    if (!isModalDisabled) {
      trackEvent({ action: 'displayed', module: 'uplift-price-context-pop-up' });
    }

    onTrack?.();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    imitateClickOnKeyEvent(e, true);
    trackUplift();
  };

  const currencySymbol = getSymbolFromCurrenciesData(currencyCode);

  const classes = cn(className, 'uplift', {
    'uplift-font': showOrFromLabel,
  });

  if (!canStartLoad) {
    return renderWithWrapper(
      <span className={classes}>
        <Skeleton {...skeletonProps} />
      </span>,
    );
  }

  return renderWithWrapper(
    <span
      aria-label={`${ariaLabel || ''} ${startLabel} ${upliftPrice} ${currencyCode}${monthLabel}`}
      className={classes}
      onKeyDown={onKeyDown}
      onMouseDown={trackUplift}
      onTouchStart={trackUplift}
      role="button"
    >
      {!upliftPrice && <Skeleton {...skeletonProps} />}
      <UpliftRoot
        aria-label={`${ariaLabel || ''} ${startLabel} ${upliftPrice} ${currencyCode}${monthLabel}`}
        ariaHidden={ariaHidden}
        className={cn(!upliftPrice && 'hidden', isHorizontal && 'upliftPriceInfo')}
        isCruiseOptionPrice={isCruiseOptionPrice}
        isLightboxDisabled={isModalDisabled}
        isTaxesIncluded={isTaxesIncluded}
        isTooltipDisabled={isTooltipDisabled}
        onReady={setUpliftPrice}
        price={price!}
        priceType={priceType}
        tripInfo={tripInfo}
      >
        {isHorizontal && (
          <>
            <span className="uplift__from">{`${startLabel} `}</span>
            <span className="modal-linkContainer">
              <span className="modal-link">
                <span className="uplift__price">
                  {currencySymbol}
                  <UpliftReceiver className="price" />
                  <span className="month">{monthLabel}</span>
                </span>
                <UpliftTooltip className="tooltip" icon={customIcon} />
              </span>
            </span>
          </>
        )}
        {!isHorizontal && (
          <>
            <span className="flex-price">
              {hasPriceTypeLabel && !!priceType && (
                <>
                  <PriceTypeMessage priceType={priceType} />{' '}
                </>
              )}
              <span className="from-label">{startLabel} </span>
              <span className="modal-link">
                {currencySymbol}
                <span>
                  <UpliftReceiver className="price" withSkeleton />
                  {monthLabel}
                </span>
              </span>
            </span>
            <UpliftTooltip aria-label="uplift modal tooltip" className="tooltip" icon={customIcon} />
          </>
        )}
      </UpliftRoot>
    </span>,
  );
};

export default Uplift;
